import React, { useState, useEffect, useRef } from "react";
import { Box, Paper, IconButton, Typography, Divider } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import MicroWellCalibrationData from "./CalibrationData";
import MicroWellParameters from "./Parameters";
import MicroWellHeader from "./Header";
import MicroWellHistory from "./History";
import { getInitialState } from "./DefaultParameters";

// MicroWell calibration endpoint: POST /api/v1/microwell/calibrate
// Math model: eMwMathModel 0 = standard sigmoid, 1/2 = assay-type variants
// (mirrors eMwMathModel enum in the backend CP_MWMath).

const APP_VERSION = "1.0.0";
const MATH_LIB_NOTE = "MicroWell sigmoid model (eMwMathModel 0/1/2)";

export default function MicroWellCalibrationLayout() {

    const [isResultsCollapsed, setResultsCollapsed] = useState(true);
    const [isCalibrationCollapsed, setCalibrationCollapsed] = useState(false);
    const [isParametersCollapsed, setParametersCollapsed] = useState(false);

    const [rows, setRows] = useState([
        { conc: "", resp: "" },
        { conc: "", resp: "" }
    ]);
    const [parameters, setParameters] = useState(getInitialState());
    const [resultDataMap, setResultDataMap] = useState(() => {
        const saved = sessionStorage.getItem("microwellCalibrationHistory");
        return saved ? JSON.parse(saved) : {};
    });

    const resultsRef = useRef(null);

    useEffect(() => {
        const hasData = resultDataMap && Object.keys(resultDataMap).length > 0;
        if (hasData) setResultsCollapsed(false);
    }, [resultDataMap]);

    return (
        <div>
            <MicroWellHeader
                rows={rows}
                parameters={parameters}
                setResultDataMap={setResultDataMap}
                onCalibrate={() => {
                    setCalibrationCollapsed(true);
                    setParametersCollapsed(true);
                    setResultsCollapsed(false);
                    setTimeout(() => {
                        resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 150);
                }}
            />

            <Box display="flex" flexDirection="column" padding={2}>

                {/* ================= CALIBRATION DATA PANEL ================= */}
                <Paper elevation={3} sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">Calibration Data</Typography>
                        <IconButton
                            size="small"
                            onClick={() => setCalibrationCollapsed(!isCalibrationCollapsed)}
                            sx={{ color: "white" }}
                        >
                            {isCalibrationCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                    </Box>
                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{ display: isCalibrationCollapsed ? "none" : "block" }}
                    >
                        <MicroWellCalibrationData rows={rows} setRows={setRows} />
                    </Box>
                </Paper>

                {/* ================= PARAMETERS PANEL ================= */}
                <Paper elevation={3} sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">Assay Parameters</Typography>
                        <IconButton
                            size="small"
                            onClick={() => setParametersCollapsed(!isParametersCollapsed)}
                            sx={{ color: "white" }}
                        >
                            {isParametersCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                    </Box>
                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{ display: isParametersCollapsed ? "none" : "block" }}
                    >
                        <MicroWellParameters parameters={parameters} setParameters={setParameters} />
                    </Box>
                </Paper>

                {/* ================= RESULTS PANEL ================= */}
                <Paper ref={resultsRef} elevation={3} sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        bgcolor="#774def"
                        color="#ffffff"
                        px={2}
                        py={1}
                    >
                        <Typography variant="subtitle1">Calibration Results</Typography>
                        <IconButton
                            size="small"
                            onClick={() => setResultsCollapsed(!isResultsCollapsed)}
                            sx={{ color: "white" }}
                        >
                            {isResultsCollapsed ? <ExpandMore /> : <ExpandLess />}
                        </IconButton>
                    </Box>
                    <Box
                        flex={1}
                        overflow="auto"
                        p={2}
                        sx={{ display: isResultsCollapsed ? "none" : "block" }}
                    >
                        <MicroWellHistory
                            resultDataMap={resultDataMap}
                            rows={rows}
                            parameters={parameters}
                            setResultDataMap={setResultDataMap}
                        />
                    </Box>
                </Paper>

                {/* ================= VERSION FOOTER ================= */}
                <Divider sx={{ mt: 3 }} />
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mt: 1,
                        color: "text.secondary",
                        fontSize: 11
                    }}
                >
                    Frontend v{APP_VERSION} {"\u2022"} {MATH_LIB_NOTE}
                    {"\u2022"} Endpoint: POST /api/v1/microwell/calibrate
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        display: "block",
                        mt: 0.5,
                        color: "text.secondary",
                        fontSize: 11
                    }}
                >
                    Math models: 0 = Standard, 1 = Variant 1, 2 = Variant 2
                    (eMwMathModel enum in backend CP_MWMath)
                </Typography>

            </Box>
        </div>
    );
}
